import { Spinner } from '@ui';
import Tooltip from '@ui/Tooltip';
import { useClubSlug } from 'components/Clubs/hooks';
import { FormInput } from 'components/Form/next';
import useModalManager from 'hooks/useModalManager';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { RecipientSuggestions, Suggestion, SuggestionValues } from './RecipientSuggestions';
import { SuggestionApplyModal } from './SuggestionApplyModal';

interface Props extends React.ComponentProps<typeof FormInput> {
    size?: any;
}

/** Query length from which suggestions are requested — the panel itself opens earlier. */
const MIN_QUERY_LENGTH = 1;

/** Delay before a keystroke turns into a request. Only the query is debounced, never the panel. */
const DEBOUNCE_MS = 250;

/**
 * A comfortable base width that the `matchReferenceWidth` middleware grows to the input's width
 * whenever the field is wider. The colours are forced because the tooltip's own base style uses
 * tw-text-gray-700 — a grey outside the accents scale — and cva concatenates instead of merging,
 * so a plain class would only win by stylesheet order.
 */
const PANEL_CLASS = 'tw-w-[26rem] tw-max-w-[90vw] !tw-border-accents-3 !tw-px-2 !tw-text-accents-9 tw-overflow-y-auto';

export const InvoiceRecipientNameField = (props: Props) => {
    const { t } = useTranslation('invoice');
    const { setValue, getValues } = useFormContext();
    const { addModal, removeModal } = useModalManager();
    const clubSlug = useClubSlug();

    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;

    const { data, isValidating } = useSWR(
        hasQuery && clubSlug ? [`invoice/${clubSlug}/suggestions`, { query }] : null,
        null,
        {
            keepPreviousData: true,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    const suggestions: Suggestion[] = useMemo(() => data?.data ?? [], [data]);

    // Turns keystrokes into a request without ever gating the panel on the response.
    const debouncedQuery = useMemo(() => debounce((value: string) => setQuery(value), DEBOUNCE_MS), []);

    useEffect(() => () => debouncedQuery.cancel(), [debouncedQuery]);

    // Resets the highlighted row whenever the result set changes underneath it.
    useEffect(() => setActiveIndex(-1), [suggestions]);

    /** Fields the suggestions cover — the only ones a clear is ever allowed to touch. */
    const scope: string[] = useMemo(() => data?.fields ?? [], [data]);
    const clearEmpty: boolean = data?.clear_empty ?? false;

    // Writes a value into the form. Kept in one place so applying and clearing behave identically.
    const write = (key: string, value: string | number | null) =>
        setValue(key, value, { shouldDirty: true, shouldValidate: true });

    // Applies the given values and blanks the given fields. Empty values are never written,
    // so a field only ever loses its content through an explicit clear.
    const applySuggestion = (values: SuggestionValues, keys?: string[], clearKeys: string[] = []) => {
        Object.entries(values)
            .filter(([key, value]) => (!keys || keys.includes(key)) && value !== null && value !== '')
            .forEach(([key, value]) => write(key, value));

        clearKeys.forEach((key) => write(key, null));
    };

    // Applies a suggestion in full, clearing the fields it has no value for when the club asked for it.
    const handleSelect = (suggestion: Suggestion) => {
        applySuggestion(
            suggestion.values,
            undefined,
            clearEmpty ? scope.filter((key) => !(key in suggestion.values)) : []
        );
        setIsOpen(false);
    };

    // Opens the review dialog where individual fields can be de-selected before applying.
    const handleAdvanced = (suggestion: Suggestion) => {
        setIsOpen(false);
        addModal({
            title: t('invoiceForm.recipientField.applyModal.title'),
            size: 50,
            content: (id) => (
                <SuggestionApplyModal
                    suggestion={suggestion}
                    current={getValues()}
                    scope={scope}
                    clearEmpty={clearEmpty}
                    onApply={(keys, clearKeys) => applySuggestion(suggestion.values, keys, clearKeys)}
                    onClose={() => removeModal(id)}
                />
            )
        });
    };

    // Moves the highlight, confirms the highlighted row or closes the panel.
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || suggestions.length === 0) {
            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const offset = event.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex((index) => (index + offset + suggestions.length) % suggestions.length);
            return;
        }

        if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            handleSelect(suggestions[activeIndex]);
            return;
        }

        if (event.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <Tooltip
            trigger="manual"
            show={isOpen}
            onShow={setIsOpen}
            placement="bottom-start"
            offset={4}
            matchReferenceWidth
            tooltipClassName={PANEL_CLASS}
            tooltip={
                <RecipientSuggestions
                    suggestions={suggestions}
                    isLoading={isValidating && suggestions.length === 0}
                    isRefreshing={isValidating && suggestions.length > 0}
                    total={data?.count ?? 0}
                    activeIndex={activeIndex}
                    onSelect={handleSelect}
                    onAdvanced={handleAdvanced}
                />
            }
        >
            <FormInput
                {...props}
                autoComplete="off"
                role="combobox"
                aria-expanded={isOpen}
                aria-autocomplete="list"
                // contentRightStyling stays on: switching it off applies a negative right margin
                // that pulls the content past the input's own padding, out to the very edge.
                contentRight={
                    isValidating ? (
                        <span className="tw-flex tw-items-center tw-text-accents-7">
                            <Spinner size="xs" color="currentColor" />
                        </span>
                    ) : undefined
                }
                onKeyDown={handleKeyDown}
                onBlur={() => setIsOpen(false)}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const value = event.target.value ?? '';

                    // The panel opens on the keystroke itself; the request follows once typing settles.
                    setIsOpen(value.trim().length >= MIN_QUERY_LENGTH);
                    debouncedQuery(value);
                }}
            />
        </Tooltip>
    );
};
